import { useMemo, useState } from 'react';
import axios from 'axios';
// Only the error PARSERS are borrowed from the api module -- not its axios
// instance, which would attach a bearer token this page must never send.
import { NON_FIELD_KEY, getErrorMessage, getFieldErrors } from '@/services/api';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { formatCurrency } from '@/utils';
import { ADDONS, NCB_SLABS, type AddonCode } from '@/utils/addons';

/**
 * Owner, 25 Sep: "it should show IDV used.. and we should be allowed to
 * change.. also allowed NCB Y and N and other things like add on.. if they
 * want".
 *
 * Posts the customer's choices to `POST /api/public/quotes/<token>/reprice/`,
 * which re-prices every insurer and cover on the same request and supersedes
 * the earlier insurer prices. The parent then re-reads its payload, so the
 * comparison redraws in place with the page's normal polling intact.
 */

export interface CustomiseState {
  idv: string | null;
  previous_ncb: number | null;
  has_claim_in_previous: boolean;
  addons: string[];
  can_reprice: boolean;
}

interface IdvOption {
  cover_type?: string | null;
  idv?: string | number | null;
  min_idv?: string | number | null;
  max_idv?: string | number | null;
}

/** The lowest insurer IDV, and the widest band across insurers. */
export function idvBounds(options: readonly IdvOption[]) {
  const num = (v: unknown) => {
    const n = Number(v);
    return v !== null && v !== undefined && v !== '' && Number.isFinite(n) && n > 0 ? n : null;
  };
  let lowestDefault: number | null = null;
  let min: number | null = null;
  let max: number | null = null;
  for (const option of options) {
    if (option.cover_type === 'tp_only') continue;
    const idv = num(option.idv);
    const low = num(option.min_idv);
    const high = num(option.max_idv);
    if (idv !== null && (lowestDefault === null || idv < lowestDefault)) lowestDefault = idv;
    if (low !== null && (min === null || low < min)) min = low;
    if (high !== null && (max === null || high > max)) max = high;
  }
  return { lowestDefault, min, max };
}

const MOBILE_QUERY = '(max-width: 639px)';

const inputClass =
  'mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white';

export function AdjustQuotePanel({
  token,
  options,
  customise,
  onRepriced,
  disabled = false,
}: {
  token: string;
  options: readonly IdvOption[];
  customise: CustomiseState;
  /** Called after a successful re-price; the parent re-reads the quote. */
  onRepriced: () => Promise<void> | void;
  /** Insurer prices are still arriving: nothing can be re-priced yet. */
  disabled?: boolean;
}) {
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const bounds = useMemo(() => idvBounds(options), [options]);
  // Blank unless the customer chose an IDV before: each insurer then keeps its
  // OWN default (shown as the placeholder) -- nothing is sent they never typed.
  const [open, setOpen] = useState(!isMobile);
  const [idv, setIdv] = useState(customise.idv ? String(Math.round(Number(customise.idv))) : '');
  const [idvTouched, setIdvTouched] = useState(false);
  const [claim, setClaim] = useState<boolean>(customise.has_claim_in_previous);
  const [ncb, setNcb] = useState<string>(customise.previous_ncb === null ? '' : String(customise.previous_ncb));
  // Like the IDV: the bonus is sent only when the customer changed it here, so
  // a panel re-drawn from the stored (possibly claim-zeroed) value never
  // overwrites what the server kept.
  const [ncbTouched, setNcbTouched] = useState(false);
  const [addons, setAddons] = useState<string[]>(customise.addons ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [done, setDone] = useState<string | null>(null);

  const toggle = (code: AddonCode) =>
    setAddons((current) => (current.includes(code) ? current.filter((c) => c !== code) : [...current, code]));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setFieldErrors({});
    setDone(null);
    const body: Record<string, unknown> = {
      has_claim_in_previous: claim,
      addons,
    };
    // Only an IDV the customer actually edited here; otherwise leave the
    // stored choice (or each insurer's default) alone.
    // Touched and cleared: back to each insurer's own default.
    if (idvTouched) body.idv = idv.trim() !== '' ? idv.trim() : null;
    if (!claim && ncbTouched && ncb !== '') body.previous_ncb = Number(ncb);
    try {
      // Every insurer is asked again inside this request, like the start.
      await axios.post(`/policy/api/public/quotes/${token}/reprice/`, body, { timeout: 115_000 });
      await onRepriced();
      setDone('Prices updated.');
    } catch (err) {
      const parsed = getFieldErrors(err);
      setFieldErrors(parsed);
      const known = ['idv', 'previous_ncb', 'has_claim_in_previous', 'addons'];
      const other = Object.entries(parsed)
        .filter(([key]) => key === NON_FIELD_KEY || !known.includes(key))
        .map(([, message]) => message);
      setError(other.length ? other.join(' ') : Object.keys(parsed).length ? null : getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const rangeHint =
    bounds.min !== null && bounds.max !== null
      ? `Leave blank for each insurer's own IDV (shown under each price). Insurers accept ${formatCurrency(bounds.min)} – ${formatCurrency(bounds.max)}; each keeps your IDV within its own range.`
      : "Leave blank for each insurer's own IDV. Each insurer keeps your IDV within its own range.";

  return (
    <section
      aria-label="Adjust your quote"
      className="mb-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800"
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls="adjust-quote-form"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-left text-sm font-semibold text-gray-900 dark:text-white"
      >
        Adjust your quote
        <span className="text-xs font-medium text-primary-600 dark:text-primary-400">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && (
        <form id="adjust-quote-form" onSubmit={submit} className="mt-3 space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="adjust-idv" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Insured declared value (IDV), ₹
              </label>
              <input
                id="adjust-idv"
                inputMode="numeric"
                type="number"
                // No native min/max: a value outside the band must still be
                // sendable -- each insurer clamps it and says so.
                min={0}
                step={1}
                className={inputClass}
                value={idv}
                placeholder={
                  bounds.lowestDefault !== null
                    ? `Insurer default (from ${formatCurrency(bounds.lowestDefault)})`
                    : "Each insurer's default"
                }
                onChange={(e) => {
                  setIdv(e.target.value);
                  setIdvTouched(true);
                }}
                aria-describedby="adjust-idv-hint"
              />
              <p id="adjust-idv-hint" className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {rangeHint}
              </p>
              {fieldErrors.idv && <p className="mt-1 text-xs text-red-600">{fieldErrors.idv}</p>}
            </div>
            <fieldset>
              <legend className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Did you make a claim last year?
              </legend>
              <div className="mt-2 flex gap-4 text-sm text-gray-700 dark:text-gray-300">
                <label className="flex items-center gap-2">
                  <input type="radio" name="adjust-claim" checked={claim} onChange={() => setClaim(true)} />
                  Yes
                </label>
                <label className="flex items-center gap-2">
                  <input type="radio" name="adjust-claim" checked={!claim} onChange={() => setClaim(false)} />
                  No
                </label>
              </div>
            </fieldset>
            {!claim && (
              <div>
                <label htmlFor="adjust-ncb" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Previous no-claim bonus
                </label>
                <select id="adjust-ncb" className={inputClass} value={ncb} onChange={(e) => {
                    setNcb(e.target.value);
                    setNcbTouched(true);
                  }}
                >
                  <option value="">Not sure</option>
                  {NCB_SLABS.map((slab) => (
                    <option key={slab} value={String(slab)}>
                      {slab}%
                    </option>
                  ))}
                </select>
                {fieldErrors.previous_ncb && <p className="mt-1 text-xs text-red-600">{fieldErrors.previous_ncb}</p>}
              </div>
            )}
          </div>
          <fieldset>
            <legend className="text-sm font-medium text-gray-700 dark:text-gray-300">Add-ons</legend>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Not every insurer offers every add-on -- the comparison shows what each one includes.
            </p>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {ADDONS.map((addon) => (
                <label key={addon.code} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={addons.includes(addon.code)}
                    onChange={() => toggle(addon.code)}
                  />
                  <span>
                    {addon.label}
                    <span className="block text-xs text-gray-500 dark:text-gray-400">{addon.description}</span>
                  </span>
                </label>
              ))}
            </div>
            {fieldErrors.addons && <p className="mt-1 text-xs text-red-600">{fieldErrors.addons}</p>}
          </fieldset>
          {error && (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          )}
          {done && !busy && (
            <p role="status" className="text-sm text-green-700 dark:text-green-300">
              {done}
            </p>
          )}
          {disabled && (
            <p role="status" className="text-sm text-gray-600 dark:text-gray-400">
              Prices are still arriving -- you can update them once every insurer has answered.
            </p>
          )}
          <button
            type="submit"
            disabled={busy || disabled}
            className="w-full rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
          >
            {busy ? 'Fetching new prices…' : 'Update prices'}
          </button>
        </form>
      )}
    </section>
  );
}
